import { Body, Controller, Delete, Get, Param, Patch, Post, Query, UseGuards } from '@nestjs/common';
import { AuthGuard } from '@nestjs/passport';
import { ApiBearerAuth, ApiOperation, ApiTags } from '@nestjs/swagger';
import { RequireModule } from '../../auth/decorators/require-module.decorator';
import { RequirePermission } from '../../auth/decorators/require-permission.decorator';
import type { LoginUserInfo } from '../../auth/dto/login.dto';
import { GetUser } from '../../auth/get-user.decorator';
import { ModuleGuard } from '../../auth/guards/module.guard';
import { PermissionGuard } from '../../auth/guards/permission.guard';
import {
  AccionAnticipoDto,
  CreateAnticipoDto,
  QueryAnticiposDto,
  UpdateAnticipoDto,
} from '../dto/anticipos.dto';
import { AnticiposService } from '../services/anticipos.service';

@ApiTags('RRHH - Anticipos de salario')
@Controller({ path: 'rrhh/anticipos', version: '1' })
@UseGuards(AuthGuard('jwt'), ModuleGuard, PermissionGuard)
@RequireModule('RRHH')
@ApiBearerAuth()
export class AnticiposController {
  constructor(private readonly service: AnticiposService) {}

  @Get()
  @RequirePermission('RRHH', 'RH_ANT_ANTICIPO_VER')
  @ApiOperation({ summary: 'Listar anticipos con filtros y paginación' })
  listar(@GetUser() user: LoginUserInfo, @Query() query: QueryAnticiposDto) {
    return this.service.listar(user.empresa_id, query);
  }

  @Get(':id')
  @RequirePermission('RRHH', 'RH_ANT_ANTICIPO_VER')
  @ApiOperation({ summary: 'Obtener detalle de anticipo' })
  obtener(@Param('id') id: string, @GetUser() user: LoginUserInfo) {
    return this.service.obtener(id, user.empresa_id);
  }

  @Post()
  @RequirePermission('RRHH', 'RH_ANT_ANTICIPO_CREAR')
  @ApiOperation({ summary: 'Solicitar nuevo anticipo (estado PENDIENTE)' })
  crear(@GetUser() user: LoginUserInfo, @Body() dto: CreateAnticipoDto) {
    return this.service.crear(user.empresa_id, dto, user.id);
  }

  @Patch(':id')
  @RequirePermission('RRHH', 'RH_ANT_ANTICIPO_EDITAR')
  @ApiOperation({ summary: 'Editar anticipo (sólo si está PENDIENTE)' })
  actualizar(
    @Param('id') id: string,
    @GetUser() user: LoginUserInfo,
    @Body() dto: UpdateAnticipoDto,
  ) {
    return this.service.actualizar(id, user.empresa_id, dto);
  }

  @Patch(':id/aprobar')
  @RequirePermission('RRHH', 'RH_ANT_ANTICIPO_EDITAR')
  @ApiOperation({ summary: 'Aprobar anticipo (PENDIENTE → APROBADO)' })
  aprobar(
    @Param('id') id: string,
    @GetUser() user: LoginUserInfo,
    @Body() dto: AccionAnticipoDto,
  ) {
    return this.service.aprobar(id, user.empresa_id, dto, user.id);
  }

  @Patch(':id/anular')
  @RequirePermission('RRHH', 'RH_ANT_ANTICIPO_EDITAR')
  @ApiOperation({ summary: 'Anular anticipo (PENDIENTE/APROBADO → ANULADO)' })
  anular(
    @Param('id') id: string,
    @GetUser() user: LoginUserInfo,
    @Body() dto: AccionAnticipoDto,
  ) {
    return this.service.anular(id, user.empresa_id, dto, user.id);
  }

  @Delete(':id')
  @RequirePermission('RRHH', 'RH_ANT_ANTICIPO_ELIMINAR')
  @ApiOperation({ summary: 'Eliminar anticipo PENDIENTE (no usar para los demás estados)' })
  eliminar(@Param('id') id: string, @GetUser() user: LoginUserInfo) {
    return this.service.eliminar(id, user.empresa_id);
  }
}
